import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import { getQuestionnaireAnswers } from "@medplum/core";
import { QuestionnaireResponse } from "@medplum/fhirtypes";
import { Loading, useMedplum } from "@medplum/react";

import { Group, Paper, Stack, Title } from "@mantine/core";

import { isValid } from "date-fns";
import { format, toDate } from "date-fns-tz";

import { TextWrapper } from "@/components/TextWrapper";
import { WordsDiff } from "@/components/WordsDiff";
import { TransferOfCareClientInformationQuestionsLinkIds } from "@/encounters/constants/transfer-of-care";
import { fetchAmendmentHistoryByQuestionnaireUrl } from "@/encounters/services/questionnaire-response";
import { Questionnaires } from "@/fhir/enums/questionnaires";

/**
 * Formats a FHIR/date-fns-tz date string for WordsDiff; empty or invalid values render blank.
 */
function formatClientDateOfBirth(value: string | undefined): string {
  if (!value) return "";
  const date = toDate(value);
  return isValid(date) ? format(date, "MM/dd/yyyy") : "";
}

const ClientInformationDiff = ({ currentVersionIndex }: { currentVersionIndex: number }) => {
  const { encounterId } = useParams<{ encounterId: string }>();
  const medplum = useMedplum();

  const [isFetchingInitialData, setIsFetchingInitialData] = useState(true);
  const [currentVersion, setCurrentVersion] = useState<QuestionnaireResponse>();
  const [previousVersion, setPreviousVersion] = useState<QuestionnaireResponse>();

  useEffect(() => {
    const getAmendmentHistory = async () => {
      medplum.invalidateSearches("QuestionnaireResponse");
      try {
        const [foundAmendmentHistory] = await fetchAmendmentHistoryByQuestionnaireUrl(
          medplum,
          Questionnaires.TransferOfCareClientInformation,
          encounterId!,
        );

        if (!foundAmendmentHistory) return;

        setCurrentVersion(foundAmendmentHistory[currentVersionIndex]);
        setPreviousVersion(foundAmendmentHistory[currentVersionIndex + 1]);
      } finally {
        setIsFetchingInitialData(false);
      }
    };
    getAmendmentHistory();
  }, [currentVersionIndex, encounterId, medplum]);

  if (isFetchingInitialData) return <Loading />;

  if (!currentVersion || !previousVersion) return null;

  const previousClientInformationAnswers = getQuestionnaireAnswers(previousVersion);
  const currentClientInformationAnswers = getQuestionnaireAnswers(currentVersion);

  const previousClientName =
    previousClientInformationAnswers[TransferOfCareClientInformationQuestionsLinkIds.FirstName]
      ?.valueString;
  const currentClientName =
    currentClientInformationAnswers[TransferOfCareClientInformationQuestionsLinkIds.FirstName]
      ?.valueString;

  const previousClientLastName =
    previousClientInformationAnswers[TransferOfCareClientInformationQuestionsLinkIds.LastName]
      ?.valueString;
  const currentClientLastName =
    currentClientInformationAnswers[TransferOfCareClientInformationQuestionsLinkIds.LastName]
      ?.valueString;

  const previousClientDateOfBirth =
    previousClientInformationAnswers[TransferOfCareClientInformationQuestionsLinkIds.DateOfBirth]
      ?.valueDateTime;
  const currentClientDateOfBirth =
    currentClientInformationAnswers[TransferOfCareClientInformationQuestionsLinkIds.DateOfBirth]
      ?.valueDateTime;

  const previousClientAddress =
    previousClientInformationAnswers[TransferOfCareClientInformationQuestionsLinkIds.Address]
      ?.valueString;
  const currentClientAddress =
    currentClientInformationAnswers[TransferOfCareClientInformationQuestionsLinkIds.Address]
      ?.valueString;

  const previousClientPhoneNumber =
    previousClientInformationAnswers[
      TransferOfCareClientInformationQuestionsLinkIds.TelephoneNumber
    ]?.valueString;
  const currentClientPhoneNumber =
    currentClientInformationAnswers[TransferOfCareClientInformationQuestionsLinkIds.TelephoneNumber]
      ?.valueString;

  const previousClientEmail =
    previousClientInformationAnswers[TransferOfCareClientInformationQuestionsLinkIds.EmailAddress]
      ?.valueString;
  const currentClientEmail =
    currentClientInformationAnswers[TransferOfCareClientInformationQuestionsLinkIds.EmailAddress]
      ?.valueString;

  const previousClientInsurance =
    previousClientInformationAnswers[TransferOfCareClientInformationQuestionsLinkIds.Insurance]
      ?.valueString;
  const currentClientInsurance =
    currentClientInformationAnswers[TransferOfCareClientInformationQuestionsLinkIds.Insurance]
      ?.valueString;

  const previousClientResponsibleParty =
    previousClientInformationAnswers[
      TransferOfCareClientInformationQuestionsLinkIds.ResponsibleParty
    ]?.valueString;
  const currentClientResponsibleParty =
    currentClientInformationAnswers[
      TransferOfCareClientInformationQuestionsLinkIds.ResponsibleParty
    ]?.valueString;

  return (
    <Paper p="md" withBorder>
      <Stack mb="lg">
        <Title order={2}>Client Information</Title>
      </Stack>
      <Group grow align="stretch">
        <Paper p="md" withBorder>
          <Stack>
            <Title mb="lg" order={4} c="gray.6">
              Previous
            </Title>
            <Stack gap="md">
              <Stack gap="0">
                <TextWrapper fw={600}>First Name</TextWrapper>
                <WordsDiff showRemoved current={currentClientName} previous={previousClientName} />
              </Stack>
              <Stack gap="0">
                <TextWrapper fw={600}>Last Name</TextWrapper>
                <WordsDiff
                  showRemoved
                  current={currentClientLastName}
                  previous={previousClientLastName}
                />
              </Stack>
              <Stack gap="0">
                <TextWrapper fw={600}>Date of Birth</TextWrapper>
                <WordsDiff
                  showRemoved
                  current={formatClientDateOfBirth(currentClientDateOfBirth)}
                  previous={formatClientDateOfBirth(previousClientDateOfBirth)}
                />
              </Stack>
              <Stack gap="0">
                <TextWrapper fw={600}>Address</TextWrapper>
                <WordsDiff
                  showRemoved
                  current={currentClientAddress}
                  previous={previousClientAddress}
                />
              </Stack>
              <Stack gap="0">
                <TextWrapper fw={600}>Phone Number</TextWrapper>
                <WordsDiff
                  showRemoved
                  current={currentClientPhoneNumber}
                  previous={previousClientPhoneNumber}
                />
              </Stack>
              <Stack gap="0">
                <TextWrapper fw={600}>Email Address</TextWrapper>
                <WordsDiff
                  showRemoved
                  current={currentClientEmail}
                  previous={previousClientEmail}
                />
              </Stack>
              <Stack gap="0">
                <TextWrapper fw={600}>Insurance</TextWrapper>
                <WordsDiff
                  showRemoved
                  current={currentClientInsurance}
                  previous={previousClientInsurance}
                />
              </Stack>
              <Stack gap="0">
                <TextWrapper fw={600}>Responsible Party</TextWrapper>
                <WordsDiff
                  showRemoved
                  current={currentClientResponsibleParty}
                  previous={previousClientResponsibleParty}
                />
              </Stack>
            </Stack>
          </Stack>
        </Paper>
        <Paper p="md" withBorder>
          <Stack>
            <Title mb="lg" order={4} c="gray.6">
              Amended
            </Title>
            <Stack gap="md">
              <Stack gap="0">
                <TextWrapper fw={600}>First Name</TextWrapper>
                <WordsDiff current={currentClientName} previous={previousClientName} />
              </Stack>
              <Stack gap="0">
                <TextWrapper fw={600}>Last Name</TextWrapper>
                <WordsDiff current={currentClientLastName} previous={previousClientLastName} />
              </Stack>
              <Stack gap="0">
                <TextWrapper fw={600}>Date of Birth</TextWrapper>
                <WordsDiff
                  current={formatClientDateOfBirth(currentClientDateOfBirth)}
                  previous={formatClientDateOfBirth(previousClientDateOfBirth)}
                />
              </Stack>
              <Stack gap="0">
                <TextWrapper fw={600}>Address</TextWrapper>
                <WordsDiff current={currentClientAddress} previous={previousClientAddress} />
              </Stack>
              <Stack gap="0">
                <TextWrapper fw={600}>Phone Number</TextWrapper>
                <WordsDiff
                  current={currentClientPhoneNumber}
                  previous={previousClientPhoneNumber}
                />
              </Stack>
              <Stack gap="0">
                <TextWrapper fw={600}>Email Address</TextWrapper>
                <WordsDiff current={currentClientEmail} previous={previousClientEmail} />
              </Stack>
              <Stack gap="0">
                <TextWrapper fw={600}>Insurance</TextWrapper>
                <WordsDiff current={currentClientInsurance} previous={previousClientInsurance} />
              </Stack>
              <Stack gap="0">
                <TextWrapper fw={600}>Responsible Party</TextWrapper>
                <WordsDiff
                  current={currentClientResponsibleParty}
                  previous={previousClientResponsibleParty}
                />
              </Stack>
            </Stack>
          </Stack>
        </Paper>
      </Group>
    </Paper>
  );
};

export default ClientInformationDiff;
